import BTThemeProvider from '@common-components/bt-theme-provider';
import { Toaster } from '@components/ui/toaster';
import { isBusyAuthStatus } from '@common-reducers/UserReducer';
import { selectUser } from '@common-reducers/UserSelectors';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { subscribeStorageAuthSync } from '@common/bt-auth/storage-auth-sync';
import { useStore } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { goTo } from 'react-chrome-extension-router';

import StatusBox from '@common-components/status-box/status-box';
import AccessForm from '@common/components/access-form/access-form';
import SubscriptionSelection from '@common/components/subscription-selection/subscription-selection';
import classes from './popup.module.scss';

export interface PopupProps {
    headerTitle?: string;
    headerSubtitle?: string;
    onClose?: () => void;
}

export const Popup = ({
    headerTitle,
    headerSubtitle,
    onClose
}: PopupProps) => {
    const user = useAppSelector(selectUser);
    const dispatch = useAppDispatch();
    const store = useStore();
    const authenticationStatus = useAppSelector((state) => state.UserReducer.authenticationStatus);

    // Live-sync the popup's auth state to the background chrome.storage hub.
    // The popup reads the hub once on mount (initAuth -> attemptJwtAuth); without this
    // listener, a popup opened BEFORE the web-app -> hub bridge completed (async, after
    // JWT auth on session restore) stayed stuck on the login form. Subscribe once on mount:
    // log in when bt-token appears/changes, log out when it's removed. The handler reads the
    // current token from the live store (not a stale closure) and is loop-guarded against
    // persistToken's own writes. No-ops where chrome.storage.onChanged is unavailable.
    useEffect(() => {
        const getCurrentToken = () => (store.getState() as any)?.UserReducer?.accessToken;
        const unsubscribe = subscribeStorageAuthSync(dispatch, getCurrentToken);
        return unsubscribe;
    }, [dispatch, store]);
    const loading = isBusyAuthStatus(authenticationStatus);

    const routes: any = useMemo(() => ({
        accessForm: () => (<AccessForm headerTitle={headerTitle} headerSubtitle={headerSubtitle} />),
        home: () => (<StatusBox onClose={onClose} />),
        subscriptionSelection: () => (<SubscriptionSelection />)
    }), [headerSubtitle, headerTitle, onClose]);

    const navigateTo = (component: any) => {
        goTo(component);
    };

    useEffect(() => {
        if (user) {
            console.log('popup found user');
            navigateTo(routes.home);
        } else {
            console.log('popup no user');
            navigateTo(routes.accessForm);
        }
    }, [routes.accessForm, routes.home, user]);

    return (
        <BTThemeProvider>
            <div className={classes.popupContainer}>
                {loading && (
                    <div className={classes.loaderContainer}>
                        <CircularProgress />
                    </div>
                )}
                {!loading && user && (
                    <StatusBox onClose={onClose} />
                )}
                {!loading && !user && (
                    <AccessForm headerTitle={headerTitle} headerSubtitle={headerSubtitle} />
                )}
            </div>
            {/* Single sonner Toaster for the popup root */}
            <Toaster />
        </BTThemeProvider>
    );
};
